import { useEffect, useState } from "react";
import { deriveSubtle } from "../util/color.ts";
import OklchColorPicker from "./OklchColorPicker.tsx";

/* Dev-only live theme editor. Every color in the app is a Bootstrap `--bs-*`
 * custom property on :root (see index.css), so overriding those on
 * document.documentElement recolors the running app instantly - buttons/badges
 * that derive from a base hue via color-mix() cascade automatically. Mounted
 * only on localhost (see `isLocalhost`), so it is never present in production.
 *
 * Inline overrides live on the <html> element and win over both the
 * `[data-bs-theme="light"]` and `[data-bs-theme="dark"]` rules, so edits are
 * tracked per active theme and re-applied whenever the site theme toggles. */

const STORE_KEY = "slpmeup:theme-dev-overrides";

type Token = { name: string; label: string; rgb?: boolean };
type Group = { title: string; tokens: Token[]; hueKey?: string };

const HUES: [string, string][] = [
  ["primary", "Primary"],
  ["success", "Success"],
  ["danger", "Danger"],
  ["warning", "Warning"],
  ["info", "Info"],
  ["secondary", "Secondary"],
];

function hueGroup([key, title]: [string, string]): Group {
  return {
    title,
    hueKey: key,
    tokens: [
      { name: `--bs-${key}`, label: "Base", rgb: true },
      { name: `--bs-${key}-text-emphasis`, label: "Text emphasis" },
      { name: `--bs-${key}-bg-subtle`, label: "Bg subtle" },
      { name: `--bs-${key}-border-subtle`, label: "Border subtle" },
    ],
  };
}

const GROUPS: Group[] = [
  ...HUES.map(hueGroup),
  {
    title: "Surfaces & text",
    tokens: [
      { name: "--bs-body-bg", label: "Body bg", rgb: true },
      { name: "--bs-secondary-bg", label: "Secondary bg", rgb: true },
      { name: "--bs-tertiary-bg", label: "Tertiary bg", rgb: true },
      { name: "--bs-border-color", label: "Border" },
      { name: "--bs-body-color", label: "Body text", rgb: true },
      { name: "--bs-emphasis-color", label: "Emphasis text" },
    ],
  },
];

const ALL_TOKENS: Token[] = GROUPS.flatMap((g) => g.tokens);
const ALL_NAMES: string[] = ALL_TOKENS.map((t) => t.name);
const BY_NAME: Record<string, Token> = Object.fromEntries(ALL_TOKENS.map((t) => [t.name, t]));
// Maps a hue's base token (e.g. `--bs-primary`) to its key, so editing the base
// can cascade to its emphasis/subtle tints.
const BASE_TO_HUE: Record<string, string> = Object.fromEntries(
  GROUPS.filter((g) => g.hueKey).map((g) => [`--bs-${g.hueKey}`, g.hueKey!]),
);

/** Strip our inline custom-property overrides (and their -rgb companions) from
 * <html> so computed styles fall back to the stylesheet. */
function removeInline(names: string[] = ALL_NAMES) {
  const el = document.documentElement;
  for (const name of names) {
    el.style.removeProperty(name);
    if (BY_NAME[name]?.rgb) el.style.removeProperty(`${name}-rgb`);
  }
}

type Overrides = Record<string, Record<string, string>>;

function currentTheme(): string {
  return document.documentElement.getAttribute("data-bs-theme") ?? "light";
}

function readVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function normalizeHex(value: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  return m ? `#${m[1].toLowerCase()}` : "#000000";
}

function hexToRgbList(value: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function loadOverrides(): Overrides {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Overrides) : {};
  } catch {
    return {};
  }
}

export default function ThemeDevPanel({ onLock }: { onLock?: () => void }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(currentTheme);
  const [overrides, setOverrides] = useState<Overrides>(loadOverrides);
  const [copied, setCopied] = useState(false);
  // The token whose in-page picker is open, plus where to anchor it (fixed,
  // computed from the swatch so it isn't clipped by the panel's scroll area).
  const [picking, setPicking] = useState<string | null>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // When on, editing a hue's base re-derives its tints live.
  const [autoDerive, setAutoDerive] = useState(true);

  const themeOverrides = overrides[theme] ?? {};
  const overrideCount = Object.keys(themeOverrides).length;

  // A swatch shows its override, else the live stylesheet value. Reset handlers
  // clear the inline property synchronously (see `clearInline`) so this reads
  // the reverted value on the next render rather than the stale override.
  function currentValue(name: string): string {
    return normalizeHex(themeOverrides[name] ?? readVar(name));
  }

  // Track the site's active theme so edits target the right token set.
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(currentTheme()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"],
    });
    return () => obs.disconnect();
  }, []);

  // Apply the active theme's overrides as inline custom properties. Clears all
  // managed tokens first so a theme switch (or removed override) reverts cleanly
  // to the stylesheet value.
  useEffect(() => {
    const el = document.documentElement;
    removeInline();
    for (const [name, value] of Object.entries(overrides[theme] ?? {})) {
      el.style.setProperty(name, value);
      if (BY_NAME[name]?.rgb) {
        const rgb = hexToRgbList(value);
        if (rgb) el.style.setProperty(`${name}-rgb`, rgb);
      }
    }
  }, [theme, overrides]);

  // Drop all inline overrides if the panel unmounts.
  useEffect(() => removeInline, []);

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(overrides));
  }, [overrides]);

  // The three derived tint overrides for a hue, from a base hex (or null if the
  // base isn't valid hex).
  function derivedTints(key: string, baseHex: string): Record<string, string> | null {
    const derived = deriveSubtle(normalizeHex(baseHex), theme === "dark" ? "dark" : "light");
    if (!derived) return null;
    return {
      [`--bs-${key}-text-emphasis`]: derived["text-emphasis"],
      [`--bs-${key}-bg-subtle`]: derived["bg-subtle"],
      [`--bs-${key}-border-subtle`]: derived["border-subtle"],
    };
  }

  function setVar(name: string, value: string) {
    setOverrides((prev) => {
      const next = { ...(prev[theme] ?? {}), [name]: value };
      const hueKey = BASE_TO_HUE[name];
      if (autoDerive && hueKey) Object.assign(next, derivedTints(hueKey, value));
      return { ...prev, [theme]: next };
    });
  }

  function openPicker(name: string, anchor: HTMLElement) {
    const r = anchor.getBoundingClientRect();
    const W = 236;
    const H = 230;
    const gap = 8;
    let left = r.left - W - gap; // prefer left of the swatch (panel hugs the right edge)
    if (left < gap) left = r.right + gap;
    left = Math.min(left, window.innerWidth - W - gap);
    const top = Math.min(Math.max(r.top, gap), window.innerHeight - H - gap);
    setPickerPos({ top, left });
    setPicking(name);
  }

  function resetVar(name: string) {
    // Clear the inline value now so the swatch re-reads the stylesheet on the
    // next render (the apply effect runs too late for this render).
    removeInline([name]);
    setOverrides((prev) => {
      const next = { ...(prev[theme] ?? {}) };
      delete next[name];
      return { ...prev, [theme]: next };
    });
  }

  function resetTheme() {
    removeInline();
    setOverrides((prev) => ({ ...prev, [theme]: {} }));
  }

  // Recompute a hue's emphasis/bg-subtle/border-subtle from its current base,
  // perceptually (OKLCH), for the active theme. Leaves the base untouched.
  function deriveHue(key: string) {
    const tints = derivedTints(key, currentValue(`--bs-${key}`));
    if (!tints) return;
    setOverrides((prev) => ({ ...prev, [theme]: { ...(prev[theme] ?? {}), ...tints } }));
  }

  function copyCss() {
    const entries = Object.entries(overrides[theme] ?? {});
    if (entries.length === 0) return;
    const selector =
      theme === "dark" ? '[data-bs-theme="dark"]' : ':root,\n[data-bs-theme="light"]';
    const lines: string[] = [];
    for (const [name, value] of entries) {
      lines.push(`  ${name}: ${value};`);
      if (BY_NAME[name]?.rgb) {
        const rgb = hexToRgbList(value);
        if (rgb) lines.push(`  ${name}-rgb: ${rgb};`);
      }
    }
    const css = `${selector} {\n${lines.join("\n")}\n}`;
    navigator.clipboard.writeText(css).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary rounded-circle shadow position-fixed d-flex align-items-center justify-content-center"
        style={{ bottom: "1rem", right: "1rem", width: 40, height: 40, zIndex: 1080 }}
        title="Theme color playground (dev only)"
        onClick={() => setOpen(true)}
      >
        <i className="bi bi-palette"></i>
      </button>
    );
  }

  return (
    <>
      {picking && (
        <OklchColorPicker
          key={picking}
          value={currentValue(picking)}
          onChange={(hex) => setVar(picking, hex)}
          onClose={() => setPicking(null)}
          style={{
            position: "fixed",
            top: pickerPos.top,
            left: pickerPos.left,
            width: 236,
            zIndex: 1090,
          }}
        />
      )}
      <div
        className="position-fixed bg-body border rounded-3 shadow-lg d-flex flex-column"
        style={{
          bottom: "1rem",
          right: "1rem",
          width: 300,
          maxHeight: "85vh",
          zIndex: 1080,
        }}
      >
        <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
          <i className="bi bi-palette"></i>
          <strong className="me-auto">Theme</strong>
          <button
            type="button"
            className={`btn btn-sm p-0 lh-1 ${autoDerive ? "text-primary" : "text-secondary"}`}
            title={`Auto-derive tints from base: ${autoDerive ? "on" : "off"}`}
            aria-pressed={autoDerive}
            onClick={() => setAutoDerive((v) => !v)}
          >
            <i className={`bi ${autoDerive ? "bi-link-45deg" : "bi-link-break"}`}></i>
          </button>
          <span className="badge bg-secondary text-uppercase" title="Editing this theme's tokens">
            {theme}
          </span>
          {onLock && (
            <button
              type="button"
              className="btn btn-sm p-0 lh-1 text-secondary"
              title="Hide the color lab (re-enter the unlock code to show it again)"
              onClick={onLock}
            >
              <i className="bi bi-lock"></i>
            </button>
          )}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setOpen(false)}
          ></button>
        </div>

        <div className="overflow-auto px-3 py-2" style={{ flex: 1 }}>
          {GROUPS.map((group) => (
            <div key={group.title} className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span
                  className="text-muted text-uppercase fw-semibold"
                  style={{ fontSize: "0.7rem" }}
                >
                  {group.title}
                </span>
                {group.hueKey && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 lh-1 text-secondary ms-auto"
                    title="Derive emphasis/subtle tints from the base color (OKLCH)"
                    onClick={() => deriveHue(group.hueKey!)}
                  >
                    <i className="bi bi-magic"></i>
                  </button>
                )}
              </div>
              {group.tokens.map((token) => {
                const overridden = token.name in themeOverrides;
                const value = currentValue(token.name);
                return (
                  <div key={token.name} className="d-flex align-items-center gap-2 mb-1">
                    <button
                      type="button"
                      title={`${token.name} - click to edit`}
                      onClick={(e) => openPicker(token.name, e.currentTarget)}
                      style={{
                        width: 28,
                        height: 24,
                        padding: 0,
                        border: `1px solid ${picking === token.name ? "var(--bs-primary)" : "var(--bs-border-color)"}`,
                        borderRadius: 4,
                        background: value,
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                    <span className="small flex-grow-1 text-truncate" title={token.name}>
                      {token.label}
                    </span>
                    <code className="small text-muted">{value}</code>
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 lh-1 text-secondary"
                      title="Reset to stylesheet value"
                      style={{ visibility: overridden ? "visible" : "hidden" }}
                      onClick={() => resetVar(token.name)}
                    >
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="d-flex align-items-center gap-2 px-3 py-2 border-top">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={copyCss}
            disabled={overrideCount === 0}
            title="Copy changed tokens as CSS for index.css"
          >
            <i className={`bi ${copied ? "bi-check-lg" : "bi-clipboard"} me-1`}></i>
            {copied ? "Copied" : `Copy CSS${overrideCount > 0 ? ` (${overrideCount})` : ""}`}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-auto"
            onClick={resetTheme}
            disabled={overrideCount === 0}
            title="Discard this theme's edits"
          >
            Reset
          </button>
        </div>
      </div>
    </>
  );
}
